import { authenticateUser, setAuthHeaders } from "@api/auth";
import { getTodos, getUserBureauCompanies, getUserCompanies } from "@api/user";
import { saveState } from "@store/local-storage";
import { mapDefaultStateFromSchema } from "@store/schema";
import { requiredParam } from "@utils/func";
import axios from "axios";

const stateSchema = {
  // Auth
  id: {
    default: "",
    saveToLocalStorage: true,
  },
  token: {
    default: "",
    saveToLocalStorage: true,
  },
  // Details
  email: {
    default: "",
    saveToLocalStorage: true,
  },
  phoneNumber: {
    default: "",
    dbKey: "phone_number",
    saveToLocalStorage: true,
  },
  firstName: {
    default: "",
    saveToLocalStorage: true,
  },
  lastName: {
    default: "",
    saveToLocalStorage: true,
  },
  profilePictureUrl: {
    default: "",
    saveToLocalStorage: true,
  },
  jobTitle: {
    default: "",
    dbKey: "job_title",
    saveToLocalStorage: true,
  },

  isStaff: {
    default: false,
    dbKey: "is_staff",
    saveToLocalStorage: true,
  },

  isSuperuser: {
    default: false,
    dbKey: "is_superuser",
    saveToLocalStorage: true,
  },
  // Companies
  companies: {
    default: [],
    saveToLocalStorage: false,
  },
  bureauCompanies: {
    default: [],
    saveToLocalStorage: false,
  },
  companiesHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },
  bureauCompaniesHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },
  todos: {
    default: [],
    saveToLocalStorage: false,
  },
  todosHasLoaded: {
    default: false,
    saveToLocalStorage: false,
  },
};

export const state = {
  ...mapDefaultStateFromSchema({
    moduleName: "user",
    stateSchema,
  }),
};

export const getters = {
  userIsLoggedIn(state) {
    return !!state.id && !!state.token;
  },
  userFullName(state) {
    return `${state.firstName} ${state.lastName}`;
  },
  userIsPatentRenewalEmployee(state) {
    return state.email.includes("patentrenewal.com");
  },
};

export const mutations = {
  SET_USER_STATE(
    state,
    { key = requiredParam("key"), newValue = requiredParam("newValue") } = {}
  ) {
    state[key] = newValue;

    if (stateSchema[key].saveToLocalStorage)
      saveState({
        key: `user.${key}`,
        state: newValue,
      });
  },
};

export const actions = {
  /*
   * Set default headers on init to make sure
   * that axios headers are set on refresh
   */
  init({ state }) {
    if (state.token) {
      setAuthHeaders({ token: state.token });
    }
  },
  logInUser({ commit }, { email, password }) {
    /*
     * Authenticate the user and
     * get the auth token and user data
     */
    return authenticateUser({
      email,
      password,
    }).then((response) => {
      // Set the default headers with users token
      const { data } = response;

      commit("SET_USER_STATE", {
        key: "token",
        newValue: data.token,
      });

      setAuthHeaders({ token: data.token });

      // Set user details
      commit("SET_USER_STATE", {
        key: "id",
        newValue: data.user.id,
      });

      commit("SET_USER_STATE", {
        key: "email",
        newValue: data.user.email,
      });

      commit("SET_USER_STATE", {
        key: "firstName",
        newValue: data.user.first_name,
      });

      commit("SET_USER_STATE", {
        key: "lastName",
        newValue: data.user.last_name,
      });

      commit("SET_USER_STATE", {
        key: "isStaff",
        newValue: data.user.is_staff,
      });

      commit("SET_USER_STATE", {
        key: "phoneNumber",
        newValue: data.user.userprofile.phone_number,
      });

      commit("SET_USER_STATE", {
        key: "isSuperuser",
        newValue: data.user.is_superuser,
      });

      if (data.user.userprofile) {
        commit("SET_USER_STATE", {
          key: "profilePictureUrl",
          newValue: data.user.userprofile.avatar_url,
        });
      }

      // Return user object
      return response.user;
    });
  },
  logOutUser({ commit, state }) {
    return Object.keys(state).forEach((key) => {
      commit("SET_USER_STATE", {
        key,
        newValue: stateSchema[key].default,
      });
    });
  },
  addTodo({ state, commit, dispatch }, todo) {
    return axios
      .post("todolist", todo)
      .then((results) => {
        let copyState = { ...state };
        let currentTodos = copyState.todos;

        currentTodos.push(todo);

        getTodos().then((response) => {
          const todos = response.data;

          commit("SET_USER_STATE", {
            key: "todos",
            newValue: todos,
          });
        });

        return results;
      })
      .catch((error) => {
        return error.response;
      });
  },
  removeTodo({ state, commit }, id, record) {
    return axios
      .delete(`todolist/${id}`)
      .then((results) => {
        let updatedTodos = state.todos.filter((item) => item.id !== id);

        commit("SET_USER_STATE", {
          key: "todos",
          newValue: updatedTodos,
        });

        return updatedTodos;
      })
      .catch((error) => {
        return error.response;
      });
  },
  fetchTodos({ commit }) {
    // Set intial todos state
    commit("SET_USER_STATE", {
      key: "todos",
      newValue: stateSchema["todos"].default,
    });
    commit("SET_USER_STATE", {
      key: "todosHasLoaded",
      newValue: false,
    });
    return getTodos().then((response) => {
      const todos = response.data;

      let formatted = response.data.map((todo) => {
        let regex = /([^@]+)/g;
        let emailFound = todo.responsible.match(regex);

        if (emailFound && emailFound[0]) {
          todo.responsible = emailFound[0];
        }
        return todo;
      });

      commit("SET_USER_STATE", {
        key: "todos",
        newValue: formatted,
      });
      commit("SET_USER_STATE", {
        key: "todosHasLoaded",
        newValue: true,
      });

      return todos;
    });
  },
  fetchUserCompanies({ state, commit }, { userId = state.id } = {}) {
    // Set intial companies state
    commit("SET_USER_STATE", {
      key: "companies",
      newValue: stateSchema["companies"].default,
    });
    commit("SET_USER_STATE", {
      key: "companiesHasLoaded",
      newValue: false,
    });

    return getUserCompanies({ userId })
      .then((response) => {
        if (userId === state.id) {
          const companies = response.data.sort((a, b) => {
            let companyNameA = a.company.name || "ZZZ";
            let companyNameB = b.company.name || "ZZZ";

            return companyNameA.localeCompare(companyNameB);
          });

          commit("SET_USER_STATE", {
            key: "companies",
            newValue: companies,
          });

          commit("SET_USER_STATE", {
            key: "companiesHasLoaded",
            newValue: true,
          });

          return companies;
        }
      })
      .catch((error) => {
        commit("SET_USER_STATE", {
          key: "companiesHasLoaded",
          newValue: true,
        });
        throw new Error(error.response);
      });
  },

  fetchUserBureauCompanies({ commit }, { userId = state.id } = {}) {
    // Set intial bureauCompanies state
    commit("SET_USER_STATE", {
      key: "bureauCompanies",
      newValue: stateSchema["bureauCompanies"].default,
    });
    commit("SET_USER_STATE", {
      key: "bureauCompaniesHasLoaded",
      newValue: false,
    });

    return getUserBureauCompanies({ userId })
      .then((response) => {
        const bureauCompanies = response.data.sort((a, b) => {
          let companyNameA = a.bureau_company.company.name || "ZZZ";
          let companyNameB = b.bureau_company.company.name || "ZZZ";

          return companyNameA.localeCompare(companyNameB);
        });
        commit("SET_USER_STATE", {
          key: "bureauCompanies",
          newValue: bureauCompanies,
        });
        commit("SET_USER_STATE", {
          key: "bureauCompaniesHasLoaded",
          newValue: true,
        });

        return bureauCompanies;
      })
      .catch((error) => {
        commit("SET_USER_STATE", {
          key: "bureauCompaniesHasLoaded",
          newValue: true,
        });
        throw new Error(error.response);
      });
  },
};
